import { useState } from 'react'
import { repository } from '../../data'
import { Modal } from '../kit'
import type { View } from './derive'
import { NetDot, useArm, useWrite } from './parts'
import { MachinePicker } from './Pickers'

/**
 * Make a group (groupId null) or change one. A group is exactly the machines
 * put in it — part of a site, the dev robots, the GPUs — and a machine can be
 * in several. The two automatic groups hold every machine of their kind.
 */
export function GroupPanel({ view, groupId, preset = [], onClose }: { view: View; groupId: string | null; preset?: readonly string[]; onClose: () => void }): JSX.Element {
  const group = groupId ? view.group(groupId) : undefined
  const [name, setName] = useState(group?.name ?? '')
  const [chosen, setChosen] = useState<ReadonlySet<string>>(new Set(preset))
  const [adding, setAdding] = useState(false)
  const arm = useArm()
  const create = useWrite(() => repository.createGroup(name, [...chosen]).then((r) => (onClose(), r)), () => `Made the group ${name.trim()} with ${chosen.size} machines`)
  const rename = useWrite(() => repository.renameGroup(groupId as string, name), () => `Renamed to ${name.trim()}`)
  const members = useWrite(
    (ids: readonly string[], on: boolean) => repository.setGroupMachines(groupId as string, ids, on),
    (ids, on) => `${on ? 'Added' : 'Took out'} ${ids.length} ${ids.length === 1 ? 'machine' : 'machines'}`,
  )
  const remove = useWrite(() => repository.deleteGroup(groupId as string).then((r) => (onClose(), r)), () => `Deleted ${group?.name}; access through it went with it`)

  if (!group) {
    return (
      <Modal
        title="New group of machines"
        subtitle="Tick the machines that belong together. Filter by site or search, then “All shown”."
        onClose={onClose}
        className="modal--sheet"
        footer={<button className="primary" disabled={!name.trim() || chosen.size === 0 || create.pending} onClick={() => create.run()}>Make the group</button>}
      >
        <div className="stack">
          <label className="stack" style={{ gap: 4 }}>
            <span className="field-label">Name</span>
            <input value={name} placeholder="Site A · dev" onChange={(e) => setName(e.currentTarget.value)} data-autofocus />
          </label>
          <MachinePicker machines={view.machines} chosen={chosen} onChange={setChosen} />
        </div>
      </Modal>
    )
  }

  const automatic = group.kind !== 'custom'
  const inside = group.machineIds.map((id) => view.machine(id)).filter((m): m is NonNullable<typeof m> => !!m)
  // Who this group lets in: teams by name, people by name — whoever its rules name.
  const teams = view.rules.filter((r) => r.groupId === group.id).map((r) => (r.teamId ? view.team(r.teamId)?.name : view.person(r.userId ?? '')?.name)).filter(Boolean)
  return (
    <Modal title={group.name} subtitle={`${inside.length} machines · let in: ${teams.length ? teams.join(', ') : 'no team yet'}`} onClose={onClose} className="modal--sheet">
      <div className="stack cx-panel-body">
        {automatic ? (
          <p className="muted">Automatic: it holds every {group.kind === 'all_robots' ? 'robot' : 'office computer'}, including ones added later. It cannot be edited or deleted.</p>
        ) : (
          <div className="row" style={{ gap: 8 }}>
            <input value={name} onChange={(e) => setName(e.currentTarget.value)} aria-label="Group name" style={{ flex: 1 }} />
            <button disabled={name.trim() === group.name || !name.trim() || rename.pending} onClick={() => rename.run()}>Rename</button>
          </div>
        )}

        {adding ? (
          <>
            <MachinePicker machines={view.machines.filter((m) => !group.machineIds.includes(m.id))} chosen={chosen} onChange={setChosen} />
            <div className="row" style={{ gap: 8 }}>
              <button onClick={() => setAdding(false)}>Cancel</button>
              <button className="primary" disabled={chosen.size === 0} onClick={() => (members.run([...chosen], true), setAdding(false), setChosen(new Set()))}>
                Add {chosen.size}
              </button>
            </div>
          </>
        ) : (
          <>
            <ul className="cx-list cx-list--scroll">
              {inside.map((m) => (
                <li key={m.id} className="cx-list__row">
                  <span className="row" style={{ gap: 10 }}>
                    <NetDot state={m.net} />
                    <span className="cx-mono">{m.name}</span>
                    <span className="muted cx-small">{m.robot?.site}</span>
                  </span>
                  {automatic ? null : (
                    <button className={arm.armed(m.id) ? 'danger is-armed' : 'danger'} onClick={() => arm.press(m.id, () => members.run([m.id], false))}>
                      {arm.armed(m.id) ? 'Sure?' : 'Take out'}
                    </button>
                  )}
                </li>
              ))}
            </ul>
            {automatic ? null : (
              <div className="row" style={{ gap: 8, justifyContent: 'space-between' }}>
                <button onClick={() => setAdding(true)}>Add machines</button>
                <button className={arm.armed('del') ? 'danger is-armed' : 'danger'} onClick={() => arm.press('del', () => remove.run())}>
                  {arm.armed('del') ? 'Press again: delete the group' : 'Delete group'}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </Modal>
  )
}
